import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../config/api';
import '../styles/AdminDashboard.css';

function AdminDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [retryingPostId, setRetryingPostId] = useState(null);
  const [retryMessage, setRetryMessage] = useState('');
  
  // Instagram accounts state
  const [instagramAccounts, setInstagramAccounts] = useState([]);
  const [instagramLoading, setInstagramLoading] = useState(false);
  const [instagramError, setInstagramError] = useState(null);
  const [connectSchool, setConnectSchool] = useState('');
  const [connectLoading, setConnectLoading] = useState(false);
  const [refreshingTokens, setRefreshingTokens] = useState({});
  
  // Schools management state
  const [schools, setSchools] = useState([]);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [schoolsError, setSchoolsError] = useState(null);
  const [showAddSchool, setShowAddSchool] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  const [schoolForm, setSchoolForm] = useState({
    school_code: '',
    school_name: '',
    display_name: '',
    username: '',
    hashtags: '',
    is_active: true
  });
  
  // Abandoned leads state
  const [abandonedLeads, setAbandonedLeads] = useState([]);
  const [abandonedLeadsLoading, setAbandonedLeadsLoading] = useState(false);
  const [abandonedLeadsError, setAbandonedLeadsError] = useState(null);
  const [abandonedLeadsCounts, setAbandonedLeadsCounts] = useState({ total: 0, contacted: 0, notContacted: 0 });
  const [abandonedLeadsTab, setAbandonedLeadsTab] = useState('not-contacted'); // 'not-contacted' or 'contacted'

  const [schoolQueueVisible, setSchoolQueueVisible] = useState(false);
  const [schoolQueueLoading, setSchoolQueueLoading] = useState(false);
  const [schoolQueueError, setSchoolQueueError] = useState(null);
  const [schoolQueueBySchool, setSchoolQueueBySchool] = useState([]);

  const [streamerTiers, setStreamerTiers] = useState([]);
  const [streamerSubmissions, setStreamerSubmissions] = useState([]);
  const [streamerStats, setStreamerStats] = useState({
    posted: 0,
    skip_line: 0,
    requeued: 0,
    posting: 0,
    failed: 0,
    visible_total: 0
  });
  const [streamerLoading, setStreamerLoading] = useState(false);
  const [streamerError, setStreamerError] = useState(null);
  const [editingStreamerTier, setEditingStreamerTier] = useState(null);

  const [comingSoon, setComingSoon] = useState(null);
  const [comingSoonLoading, setComingSoonLoading] = useState(false);
  const [comingSoonError, setComingSoonError] = useState(null);
  const [togglingComingSoon, setTogglingComingSoon] = useState({});

  useEffect(() => {
    fetchAnalytics();
    // Only fetch on mount, no auto-refresh
    // User can manually refresh with the button
  }, []);
  
  // Fetch Instagram accounts when switching to that tab
  useEffect(() => {
    if (activeTab === 'instagram') {
      fetchInstagramAccounts();
    }
    if (activeTab === 'manage-schools') {
      fetchSchools();
      // Also fetch Instagram accounts to show connection status
      if (instagramAccounts.length === 0) {
        fetchInstagramAccounts();
      }
    }
    if (activeTab === 'abandoned-leads') {
      fetchAbandonedLeads();
    }
    if (activeTab === 'streamer') {
      fetchStreamerData();
    }
    if (activeTab === 'coming-soon') {
      fetchComingSoonCampaign();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchStreamerData = async () => {
    setStreamerLoading(true);
    setStreamerError(null);
    try {
      const [tiersRes, subsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/streamer/tiers`),
        fetch(`${API_BASE_URL}/api/streamer/submissions`)
      ]);
      const tiersData = await tiersRes.json();
      const subsData = await subsRes.json();
      if (tiersData.status === 'success') setStreamerTiers(tiersData.data);
      if (subsData.status === 'success') {
        setStreamerSubmissions(subsData.data || []);
        setStreamerStats(subsData.stats || {
          posted: 0,
          skip_line: 0,
          requeued: 0,
          posting: 0,
          failed: 0,
          visible_total: 0
        });
      }
    } catch (err) {
      setStreamerError('Failed to load streamer data');
      console.error(err);
    } finally {
      setStreamerLoading(false);
    }
  };

  const handleSaveStreamerTier = async (tier) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/streamer/tiers/${tier.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          base_price: Math.round(parseFloat(tier.base_price_dollars) * 100),
          min_price: Math.round(parseFloat(tier.min_price_dollars) * 100),
          max_price: Math.round(parseFloat(tier.max_price_dollars) * 100),
          name: tier.name,
          description: tier.description
        })
      });
      const data = await response.json();
      if (data.status === 'success') {
        setEditingStreamerTier(null);
        fetchStreamerData();
      } else {
        alert(data.message || 'Failed to save tier');
      }
    } catch (err) {
      alert('Failed to save tier');
    }
  };

  const fetchComingSoonCampaign = async () => {
    setComingSoonLoading(true);
    setComingSoonError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/coming-soon-campaign`);
      const data = await response.json();
      if (data.status === 'success') {
        setComingSoon(data.data);
      } else {
        setComingSoonError(data.message || 'Failed to load Class of 2031 campaign');
      }
    } catch (err) {
      setComingSoonError('Failed to connect to the server');
      console.error(err);
    } finally {
      setComingSoonLoading(false);
    }
  };

  const handleToggleComingSoon = async (schoolCode, enabled) => {
    if (enabled && !window.confirm(
      `Turn on Class of 2031 posting for ${schoolCode}?\n\nThis account will post a black image with “Class of 2031 coming soon...” 35 times per day between 9:00am and 11:00pm ET.`
    )) {
      return;
    }

    setTogglingComingSoon((prev) => ({ ...prev, [schoolCode]: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/coming-soon-campaign/${schoolCode}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled })
      });
      const data = await response.json();
      if (data.status === 'success') {
        await fetchComingSoonCampaign();
        if (schools.length > 0) {
          fetchSchools();
        }
      } else {
        alert(data.message || 'Failed to update campaign');
      }
    } catch (err) {
      alert('Failed to update campaign');
      console.error(err);
    } finally {
      setTogglingComingSoon((prev) => ({ ...prev, [schoolCode]: false }));
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/analytics`);
      const data = await response.json();
      
      if (data.status === 'success') {
        setAnalytics(data.data);
        setError(null);
      } else {
        setError(data.message || 'Failed to fetch analytics');
      }
    } catch (err) {
      setError('Failed to connect to the server');
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRetrySingleFailedPost = async (postId) => {
    if (!window.confirm('Retry this post to Instagram now?')) {
      return;
    }

    setRetryingPostId(postId);
    setRetryMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/retry-failed-post/${postId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        setRetryMessage('✅ Posted successfully. Refreshing…');
        await fetchAnalytics();
        setRetryMessage('');
      } else {
        setRetryMessage(`❌ ${data.message || 'Retry failed'}`);
      }
    } catch (err) {
      setRetryMessage('❌ Failed to retry post');
      console.error('Retry error:', err);
    } finally {
      setRetryingPostId(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  /** Eastern Time, hours + minutes only (no seconds). */
  const formatScheduledEst = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatInstagramHandleCell = (handle) => {
    if (!handle || String(handle).trim() === '') return '—';
    const clean = String(handle).replace(/^@+/, '').trim();
    return `@${clean}`;
  };

  const fetchSchoolQueue = async () => {
    setSchoolQueueLoading(true);
    setSchoolQueueError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/queue/by-school`);
      const data = await response.json();
      if (data.status === 'success' && data.data?.schools) {
        setSchoolQueueBySchool(data.data.schools);
        setSchoolQueueVisible(true);
      } else {
        setSchoolQueueError(data.message || 'Failed to load queue');
      }
    } catch (err) {
      setSchoolQueueError('Failed to connect to the server');
      console.error('School queue fetch error:', err);
    } finally {
      setSchoolQueueLoading(false);
    }
  };

  const formatCurrency = (amountInCents) => {
    const dollars = (amountInCents || 0) / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(dollars);
  };

  // Instagram account management functions
  const fetchInstagramAccounts = async () => {
    setInstagramLoading(true);
    setInstagramError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/instagram/overview`);
      const data = await response.json();
      
      if (data.status === 'success') {
        setInstagramAccounts(data.data.accounts || []);
      } else {
        setInstagramError(data.message || 'Failed to fetch Instagram accounts');
      }
    } catch (err) {
      setInstagramError('Failed to connect to server');
      console.error('Instagram accounts fetch error:', err);
    } finally {
      setInstagramLoading(false);
    }
  };

  const handleConnectAccount = async () => {
    if (!connectSchool.trim()) {
      alert('Please enter a school code');
      return;
    }
    
    setConnectLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/instagram/auth/${connectSchool.trim()}`);
      const data = await response.json();
      
      if (data.status === 'success') {
        // Open the OAuth URL in a new window
        window.open(data.data.authUrl, '_blank', 'width=600,height=700');
        alert(`OAuth window opened for ${connectSchool}. Complete the authorization in the popup, then click "Refresh" to see the new account.`);
        setConnectSchool('');
      } else {
        alert(`Failed to generate OAuth URL: ${data.message}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
      console.error('Connect account error:', err);
    } finally {
      setConnectLoading(false);
    }
  };

  const handleRefreshToken = async (school) => {
    setRefreshingTokens(prev => ({ ...prev, [school]: true }));
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/instagram/accounts/${school}/refresh`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.status === 'success') {
        alert(`Token refreshed for ${school}`);
        fetchInstagramAccounts();
      } else {
        const msg = data.reconnect_required
          ? `${data.message}\n\nYou can disconnect this account below and reconnect it after confirming your Facebook/Instagram account.`
          : `Failed to refresh token: ${data.message}`;
        alert(msg);
      }
    } catch (err) {
      alert(`Error refreshing token: ${err.message}`);
    } finally {
      setRefreshingTokens(prev => ({ ...prev, [school]: false }));
    }
  };

  const handleDisconnectAccount = async (school, username) => {
    if (!window.confirm(`Are you sure you want to disconnect @${username} from ${school}? This will prevent posting to this account.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/instagram/accounts/${school}/disconnect`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.status === 'success') {
        alert(`Disconnected @${username} from ${school}`);
        fetchInstagramAccounts();
      } else {
        alert(`Failed to disconnect: ${data.message}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const getTokenStatus = (account) => {
    if (!account.token_expires_at) {
      return { status: 'unknown', label: 'Unknown', className: 'warning' };
    }
    
    const expiresAt = new Date(account.token_expires_at);
    const now = new Date();
    const daysUntilExpiry = Math.floor((expiresAt - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { status: 'expired', label: 'Expired', className: 'error' };
    } else if (daysUntilExpiry <= 7) {
      return { status: 'expiring', label: `Expires in ${daysUntilExpiry}d`, className: 'warning' };
    } else {
      return { status: 'healthy', label: `${daysUntilExpiry}d remaining`, className: 'success' };
    }
  };

  // Schools management functions
  const fetchSchools = async () => {
    setSchoolsLoading(true);
    setSchoolsError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/schools`);
      const data = await response.json();
      
      if (data.status === 'success') {
        setSchools(data.data || []);
      } else {
        setSchoolsError(data.message || 'Failed to fetch schools');
      }
    } catch (err) {
      setSchoolsError('Failed to connect to server');
      console.error('Schools fetch error:', err);
    } finally {
      setSchoolsLoading(false);
    }
  };

  const resetSchoolForm = () => {
    setSchoolForm({
      school_code: '',
      school_name: '',
      display_name: '',
      username: '',
      hashtags: '',
      is_active: true
    });
    setEditingSchool(null);
    setShowAddSchool(false);
  };

  const handleEditSchool = (school) => {
    setSchoolForm({
      school_code: school.school_code,
      school_name: school.school_name,
      display_name: school.display_name,
      username: school.username || '',
      hashtags: Array.isArray(school.hashtags) ? school.hashtags.join(', ') : (school.hashtags || ''),
      is_active: school.is_active
    });
    setEditingSchool(school.school_code);
    setShowAddSchool(true);
  };

  const handleSaveSchool = async () => {
    if (!schoolForm.school_code.trim() || !schoolForm.school_name.trim() || !schoolForm.display_name.trim()) {
      alert('School code, name, and display name are required');
      return;
    }
    
    try {
      const hashtagsArray = schoolForm.hashtags
        .split(',')
        .map(h => h.trim())
        .filter(h => h.length > 0)
        .map(h => h.startsWith('#') ? h : `#${h}`);
      
      const payload = {
        school_code: schoolForm.school_code.toUpperCase(),
        school_name: schoolForm.school_name,
        display_name: schoolForm.display_name,
        username: schoolForm.username || null,
        hashtags: hashtagsArray,
        is_active: schoolForm.is_active
      };
      
      const url = editingSchool 
        ? `${API_BASE_URL}/api/schools/${editingSchool}`
        : `${API_BASE_URL}/api/schools`;
      
      const response = await fetch(url, {
        method: editingSchool ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        alert(editingSchool ? 'School updated!' : 'School created!');
        resetSchoolForm();
        fetchSchools();
      } else {
        alert(`Error: ${data.message}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleToggleSchoolActive = async (school) => {
    const action = school.is_active ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} ${school.school_name}?`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/schools/${school.school_code}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !school.is_active })
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        fetchSchools();
      } else {
        alert(`Error: ${data.message}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleDisconnectInstagramFromSchool = async (schoolCode) => {
    if (!window.confirm(`Are you sure you want to disconnect Instagram from ${schoolCode}? The school will remain but won't be able to post until reconnected.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/instagram/accounts/${schoolCode}/disconnect`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.status === 'success') {
        alert(`Instagram disconnected from ${schoolCode}`);
        fetchSchools();
        // Also refresh Instagram accounts if that tab was loaded
        if (instagramAccounts.length > 0) {
          fetchInstagramAccounts();
        }
      } else {
        alert(`Failed to disconnect: ${data.message}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const getSchoolInstagramStatus = (schoolCode) => {
    const account = instagramAccounts.find(a => a.school === schoolCode && a.is_active);
    if (account) {
      return { connected: true, username: account.username };
    }
    return { connected: false };
  };

  // Abandoned leads functions
  const fetchAbandonedLeads = async () => {
    setAbandonedLeadsLoading(true);
    setAbandonedLeadsError(null);
    
    try {
      const contacted = abandonedLeadsTab === 'contacted' ? 'true' : 'false';
      const response = await fetch(`${API_BASE_URL}/api/admin/abandoned-leads?contacted=${contacted}`);
      const data = await response.json();
      
      if (data.status === 'success') {
        setAbandonedLeads(data.data.leads || []);
        setAbandonedLeadsCounts(data.data.counts || { total: 0, contacted: 0, notContacted: 0 });
      } else {
        setAbandonedLeadsError(data.message || 'Failed to fetch abandoned leads');
      }
    } catch (err) {
      setAbandonedLeadsError('Failed to connect to server');
      console.error('Abandoned leads fetch error:', err);
    } finally {
      setAbandonedLeadsLoading(false);
    }
  };

  const handleMarkContacted = async (leadId, contacted) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/abandoned-leads/${leadId}/contact`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contacted })
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        // Refresh the list
        fetchAbandonedLeads();
      } else {
        alert(`Error: ${data.message}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/abandoned-leads/${leadId}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        fetchAbandonedLeads();
      } else {
        alert(`Error: ${data.message}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  // Refetch when switching between contacted/not-contacted tabs
  useEffect(() => {
    if (activeTab === 'abandoned-leads') {
      fetchAbandonedLeads();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abandonedLeadsTab]);

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="error-container">
          <h2>❌ Error</h2>
          <p>{error}</p>
          <button onClick={fetchAnalytics} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <h1>📊 Admin Analytics Dashboard</h1>
        <button onClick={fetchAnalytics} className="refresh-button" disabled={loading}>
          🔄 Refresh
        </button>
      </header>

      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab ${activeTab === 'schools' ? 'active' : ''}`}
          onClick={() => setActiveTab('schools')}
        >
          School Stats
        </button>
        <button 
          className={`tab ${activeTab === 'manage-schools' ? 'active' : ''}`}
          onClick={() => setActiveTab('manage-schools')}
        >
          🏫 Manage Schools
        </button>
        <button 
          className={`tab ${activeTab === 'queue' ? 'active' : ''}`}
          onClick={() => setActiveTab('queue')}
        >
          Queue
        </button>
        <button 
          className={`tab ${activeTab === 'failed' ? 'active' : ''}`}
          onClick={() => setActiveTab('failed')}
        >
          Failed Posts ({analytics?.failedPosts?.length || 0})
        </button>
        <button 
          className={`tab ${activeTab === 'instagram' ? 'active' : ''}`}
          onClick={() => setActiveTab('instagram')}
        >
          📸 Instagram Accounts
        </button>
        <button 
          className={`tab ${activeTab === 'abandoned-leads' ? 'active' : ''}`}
          onClick={() => setActiveTab('abandoned-leads')}
        >
          👤 Abandoned Leads ({abandonedLeadsCounts.notContacted})
        </button>
        <button 
          className={`tab ${activeTab === 'streamer' ? 'active' : ''}`}
          onClick={() => setActiveTab('streamer')}
        >
          🎮 UniStreaming
        </button>
        <button 
          className={`tab ${activeTab === 'coming-soon' ? 'active' : ''}`}
          onClick={() => setActiveTab('coming-soon')}
        >
          ⬛ Class of 2031
        </button>
      </div>

      {activeTab === 'overview' && (
        <div className="tab-content">
          <div className="stats-grid">
            <div className="stat-card">
              <h3>📅 Yesterday</h3>
              <div className="stat-number">{analytics.yesterday.total}</div>
              <div className="stat-details">
                <span className="success">✅ {analytics.yesterday.completed} completed</span>
                <span className="error">❌ {analytics.yesterday.failed} failed</span>
              </div>
            </div>

            <div className="stat-card">
              <h3>📆 Last Week</h3>
              <div className="stat-number">{analytics.lastWeek.total}</div>
              <div className="stat-details">
                <span className="success">✅ {analytics.lastWeek.completed} completed</span>
                <span className="error">❌ {analytics.lastWeek.failed} failed</span>
              </div>
            </div>

            <div className="stat-card">
              <h3>📊 Last Month</h3>
              <div className="stat-number">{analytics.lastMonth.total}</div>
              <div className="stat-details">
                <span className="success">✅ {analytics.lastMonth.completed} completed</span>
                <span className="error">❌ {analytics.lastMonth.failed} failed</span>
              </div>
            </div>

            <div className="stat-card highlight">
              <h3>🌟 All Time</h3>
              <div className="stat-number">{analytics.allTime.total}</div>
              <div className="stat-details">
                <span className="success">✅ {analytics.allTime.completed} completed</span>
                <span className="error">❌ {analytics.allTime.failed} failed</span>
              </div>
            </div>

            <div className="stat-card newsletter-card">
              <h3>📬 Newsletter Subscribers</h3>
              <div className="stat-number">{analytics.newsletterSubscribers ?? 0}</div>
              <div className="stat-details">
                <span className="info">Students opted in</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'schools' && (
        <div className="tab-content">
          <div className="schools-header">
            <h2>Posts by School</h2>
            <div className="total-revenue-badge">
              💰 Total Revenue: {formatCurrency(analytics.totalRevenue)}
            </div>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>School</th>
                  <th>Revenue</th>
                  <th>% of Total</th>
                  <th>Total Posts</th>
                  <th>Completed</th>
                  <th>Failed</th>
                  <th>Queued</th>
                  <th>Posting</th>
                </tr>
              </thead>
              <tbody>
                {analytics.postsPerSchool.map((school, index) => (
                  <tr key={index}>
                    <td className="school-name">{school.school}</td>
                    <td className="revenue">{formatCurrency(school.total_revenue)}</td>
                    <td className="percentage">
                      <div className="percentage-bar-container">
                        <div 
                          className="percentage-bar" 
                          style={{ width: `${Math.min(school.revenue_percentage, 100)}%` }}
                        ></div>
                        <span className="percentage-text">{school.revenue_percentage}%</span>
                      </div>
                    </td>
                    <td>{school.total_posts}</td>
                    <td className="success">{school.completed_posts}</td>
                    <td className="error">{school.failed_posts}</td>
                    <td className="warning">{school.queued_posts}</td>
                    <td className="info">{school.posting_posts}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'manage-schools' && (
        <div className="tab-content">
          <div className="manage-schools-header">
            <h2>🏫 Manage Schools</h2>
            <div className="header-actions">
              <button 
                onClick={fetchSchools} 
                className="refresh-button"
                disabled={schoolsLoading}
              >
                🔄 Refresh
              </button>
              <button 
                onClick={() => { resetSchoolForm(); setShowAddSchool(true); }}
                className="add-school-button"
              >
                ➕ Add School
              </button>
            </div>
          </div>

          {/* Add/Edit School Form */}
          {showAddSchool && (
            <div className="school-form-container">
              <h3>{editingSchool ? `Edit ${editingSchool}` : 'Add New School'}</h3>
              <div className="school-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>School Code *</label>
                    <input
                      type="text"
                      placeholder="e.g., UGA"
                      value={schoolForm.school_code}
                      onChange={(e) => setSchoolForm({ ...schoolForm, school_code: e.target.value.toUpperCase() })}
                      disabled={!!editingSchool}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>School Name *</label>
                    <input
                      type="text"
                      placeholder="e.g., University of Georgia"
                      value={schoolForm.school_name}
                      onChange={(e) => setSchoolForm({ ...schoolForm, school_name: e.target.value })}
                      className="form-input"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Display Name *</label>
                    <input
                      type="text"
                      placeholder="e.g., UGA"
                      value={schoolForm.display_name}
                      onChange={(e) => setSchoolForm({ ...schoolForm, display_name: e.target.value })}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Instagram Username</label>
                    <input
                      type="text"
                      placeholder="e.g., ugabios"
                      value={schoolForm.username}
                      onChange={(e) => setSchoolForm({ ...schoolForm, username: e.target.value })}
                      className="form-input"
                    />
                  </div>
                </div>
                <div className="form-group full-width">
                  <label>Hashtags (comma separated)</label>
                  <input
                    type="text"
                    placeholder="e.g., UGA, GoDawgs, Georgia, Bulldogs"
                    value={schoolForm.hashtags}
                    onChange={(e) => setSchoolForm({ ...schoolForm, hashtags: e.target.value })}
                    className="form-input"
                  />
                  <span className="form-hint">Don't include # - they'll be added automatically</span>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={schoolForm.is_active}
                      onChange={(e) => setSchoolForm({ ...schoolForm, is_active: e.target.checked })}
                    />
                    Active (accepting submissions)
                  </label>
                </div>
                <div className="form-actions">
                  <button onClick={resetSchoolForm} className="cancel-button">
                    Cancel
                  </button>
                  <button onClick={handleSaveSchool} className="save-button">
                    {editingSchool ? 'Update School' : 'Create School'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {schoolsLoading && (
            <div className="loading-container" style={{ minHeight: '200px' }}>
              <div className="loading-spinner"></div>
              <p>Loading schools...</p>
            </div>
          )}

          {/* Error State */}
          {schoolsError && (
            <div className="retry-message error">
              ❌ {schoolsError}
            </div>
          )}

          {/* Schools List */}
          {!schoolsLoading && !schoolsError && (
            <>
              {schools.length === 0 ? (
                <div className="empty-state">
                  <p>📭 No schools configured yet</p>
                  <p style={{ fontSize: '0.9rem', color: '#999', marginTop: '0.5rem' }}>
                    Click "Add School" to create your first school
                  </p>
                </div>
              ) : (
                <div className="schools-management-grid">
                  {schools.map((school) => {
                    const igStatus = getSchoolInstagramStatus(school.school_code);
                    return (
                      <div key={school.id} className={`school-card ${!school.is_active ? 'inactive' : ''}`}>
                        <div className="school-card-header">
                          <div className="school-card-info">
                            <h3>{school.school_code}</h3>
                            <span className="school-full-name">{school.school_name}</span>
                          </div>
                          <div className="school-card-badges">
                            <span className={`status-badge ${school.is_active ? 'active' : 'inactive'}`}>
                              {school.is_active ? '✓ Active' : '✗ Inactive'}
                            </span>
                            {school.coming_soon_campaign && (
                              <span className="status-badge coming-soon">Class of 2031</span>
                            )}
                          </div>
                        </div>
                        
                        <div className="school-card-details">
                          <div className="detail-row">
                            <span className="detail-label">Display Name:</span>
                            <span className="detail-value">{school.display_name}</span>
                          </div>
                          {school.username && (
                            <div className="detail-row">
                              <span className="detail-label">IG Username:</span>
                              <span className="detail-value">@{school.username}</span>
                            </div>
                          )}
                          <div className="detail-row">
                            <span className="detail-label">Instagram:</span>
                            <span className={`detail-value ${igStatus.connected ? 'success' : 'warning'}`}>
                              {igStatus.connected ? `✓ Connected (@${igStatus.username})` : '✗ Not Connected'}
                            </span>
                          </div>
                          {school.hashtags && school.hashtags.length > 0 && (
                            <div className="detail-row hashtags-row">
                              <span className="detail-label">Hashtags:</span>
                              <span className="detail-value hashtags">
                                {Array.isArray(school.hashtags) 
                                  ? school.hashtags.slice(0, 3).join(' ') + (school.hashtags.length > 3 ? ` +${school.hashtags.length - 3}` : '')
                                  : school.hashtags}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        <div className="school-card-actions">
                          <button
                            onClick={() => handleEditSchool(school)}
                            className="action-button edit"
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => handleToggleSchoolActive(school)}
                            className={`action-button ${school.is_active ? 'deactivate' : 'activate'}`}
                          >
                            {school.is_active ? '⏸️ Deactivate' : '▶️ Activate'}
                          </button>
                          {igStatus.connected && (
                            <button
                              onClick={() => handleDisconnectInstagramFromSchool(school.school_code)}
                              className="action-button disconnect"
                            >
                              🔌 Disconnect IG
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Help Section */}
          <div className="schools-help-section">
            <h3>ℹ️ School Management Guide</h3>
            <ul>
              <li><strong>School Code:</strong> Unique identifier used in URLs and database (e.g., UGA, PSU). Cannot be changed after creation.</li>
              <li><strong>Display Name:</strong> Shown in captions and UI elements.</li>
              <li><strong>Instagram Username:</strong> The @handle for the school's Instagram account.</li>
              <li><strong>Hashtags:</strong> Added to every post caption for this school.</li>
              <li><strong>Disconnect Instagram:</strong> Removes the Instagram connection but keeps the school. You can reconnect later from the Instagram Accounts tab.</li>
            </ul>
          </div>
        </div>
      )}

      {activeTab === 'queue' && (
        <div className="tab-content">
          <h2>Posts Queued by Day (Next 14 Days)</h2>
          {analytics.queueByDay.length === 0 ? (
            <div className="empty-state">
              <p>📭 No posts currently queued</p>
            </div>
          ) : (
            <div className="queue-list">
              {analytics.queueByDay.map((day, index) => (
                <div key={index} className="queue-item">
                  <div className="queue-date">{formatDate(day.date)}</div>
                  <div className="queue-count">
                    <span className="count-badge">{day.count}</span>
                    <span className="count-label">post{day.count !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="school-queue-section">
            <h2>Full queue by school</h2>
            <p className="school-queue-hint">
              Instagram handle and scheduled post time in Eastern Time (US — EST or EDT). Sorted soonest first within each school; schools ordered by the next upcoming post.
            </p>
            {!schoolQueueVisible ? (
              <button
                type="button"
                className="school-queue-load-button"
                onClick={fetchSchoolQueue}
                disabled={schoolQueueLoading}
              >
                {schoolQueueLoading ? 'Loading…' : 'Show queue by school'}
              </button>
            ) : (
              <>
                <div className="school-queue-toolbar">
                  <button
                    type="button"
                    className="school-queue-refresh-button"
                    onClick={fetchSchoolQueue}
                    disabled={schoolQueueLoading}
                  >
                    {schoolQueueLoading ? 'Refreshing…' : 'Refresh'}
                  </button>
                </div>
                {schoolQueueError && (
                  <p className="school-queue-error">❌ {schoolQueueError}</p>
                )}
                {!schoolQueueLoading && !schoolQueueError && schoolQueueBySchool.length === 0 && (
                  <p className="empty-state">📭 No queued posts found.</p>
                )}
                {!schoolQueueLoading && schoolQueueBySchool.length > 0 && (
                  <div className="school-queue-schools">
                    {schoolQueueBySchool.map((block) => (
                      <div key={block.school} className="school-queue-block">
                        <h3 className="school-queue-school-title">
                          {block.display_name}{' '}
                          <span className="school-queue-code">({block.school})</span>
                        </h3>
                        <table className="school-queue-table">
                          <thead>
                            <tr>
                              <th>Instagram</th>
                              <th>Scheduled (Eastern)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {block.queue.map((row, idx) => (
                              <tr key={`${block.school}-${idx}-${row.scheduled_time || idx}`}>
                                <td>{formatInstagramHandleCell(row.instagram_handle)}</td>
                                <td>{formatScheduledEst(row.scheduled_time)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {activeTab === 'failed' && (
        <div className="tab-content">
          <div className="failed-header">
            <h2>Failed Posts</h2>
          </div>
          
          {retryMessage && (
            <div className={`retry-message ${retryMessage.includes('✅') ? 'success' : 'error'}`}>
              {retryMessage}
            </div>
          )}

          {analytics.failedPosts.length === 0 ? (
            <div className="empty-state">
              <p>✅ No failed posts! Everything is running smoothly.</p>
            </div>
          ) : (
            <div className="failed-posts-list">
              {analytics.failedPosts.map((post) => (
                <div key={post.id} className="failed-post-card">
                  <div className="failed-post-header">
                    <h3>{post.name}</h3>
                    <div className="failed-post-header-actions">
                      <span className="school-badge">{post.school}</span>
                      <button
                        type="button"
                        onClick={() => handleRetrySingleFailedPost(post.id)}
                        className="retry-single-button"
                        disabled={retryingPostId !== null}
                      >
                        {retryingPostId === post.id ? '⏳ Retrying…' : '🔄 Retry'}
                      </button>
                    </div>
                  </div>
                  <div className="failed-post-details">
                    <p><strong>Email:</strong> {post.email}</p>
                    <p><strong>Instagram:</strong> @{post.instagram_handle || 'N/A'}</p>
                    <p><strong>Failed:</strong> {formatDateTime(post.updated_at)}</p>
                    <p><strong>Submitted:</strong> {formatDateTime(post.created_at)}</p>
                  </div>
                  {post.posting_error && (
                    <div className="error-message">
                      <strong>Error:</strong> {post.posting_error}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'instagram' && (
        <div className="tab-content">
          <div className="instagram-header">
            <h2>📸 Instagram Accounts</h2>
            <button 
              onClick={fetchInstagramAccounts} 
              className="refresh-button"
              disabled={instagramLoading}
            >
              🔄 Refresh
            </button>
          </div>

          {/* Connect New Account Section */}
          <div className="connect-account-section">
            <h3>Connect New Account</h3>
            <p className="connect-description">
              Enter a school code to start the Instagram OAuth flow. The account owner will authorize access in a popup window.
            </p>
            <div className="connect-form">
              <input
                type="text"
                placeholder="School code (e.g., PSU, UGA)"
                value={connectSchool}
                onChange={(e) => setConnectSchool(e.target.value.toUpperCase())}
                className="connect-input"
                disabled={connectLoading}
              />
              <button
                onClick={handleConnectAccount}
                className="connect-button"
                disabled={connectLoading || !connectSchool.trim()}
              >
                {connectLoading ? '⏳ Generating...' : '🔗 Connect Account'}
              </button>
            </div>
          </div>

          {/* Loading State */}
          {instagramLoading && (
            <div className="loading-container" style={{ minHeight: '200px' }}>
              <div className="loading-spinner"></div>
              <p>Loading Instagram accounts...</p>
            </div>
          )}

          {/* Error State */}
          {instagramError && (
            <div className="retry-message error">
              ❌ {instagramError}
            </div>
          )}

          {/* Accounts List */}
          {!instagramLoading && !instagramError && (
            <>
              {instagramAccounts.length === 0 ? (
                <div className="empty-state">
                  <p>📭 No Instagram accounts connected yet</p>
                  <p style={{ fontSize: '0.9rem', color: '#999', marginTop: '0.5rem' }}>
                    Use the form above to connect your first account
                  </p>
                </div>
              ) : (
                <div className="instagram-accounts-grid">
                  {instagramAccounts.map((account) => {
                    const tokenStatus = getTokenStatus(account);
                    return (
                      <div key={account.id} className={`instagram-account-card ${!account.is_active ? 'inactive' : ''}`}>
                        <div className="account-header">
                          <div className="account-info">
                            <h3>@{account.username}</h3>
                            <span className="school-badge">{account.school}</span>
                          </div>
                          <span className={`status-badge ${account.is_active ? 'active' : 'inactive'}`}>
                            {account.is_active ? '✓ Active' : '✗ Inactive'}
                          </span>
                        </div>
                        
                        <div className="account-details">
                          <div className="detail-row">
                            <span className="detail-label">Account Type:</span>
                            <span className="detail-value">{account.account_type || 'Business'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Token Status:</span>
                            <span className={`detail-value ${tokenStatus.className}`}>
                              {tokenStatus.label}
                            </span>
                          </div>
                          {account.last_token_refresh && (
                            <div className="detail-row">
                              <span className="detail-label">Last Refresh:</span>
                              <span className="detail-value">{formatDateTime(account.last_token_refresh)}</span>
                            </div>
                          )}
                          <div className="detail-row">
                            <span className="detail-label">Connected:</span>
                            <span className="detail-value">{formatDateTime(account.created_at)}</span>
                          </div>
                        </div>
                        
                        <div className="account-actions">
                          <button
                            onClick={() => handleRefreshToken(account.school)}
                            className="action-button refresh"
                            disabled={refreshingTokens[account.school]}
                          >
                            {refreshingTokens[account.school] ? '⏳' : '🔄'} Refresh Token
                          </button>
                          <button
                            onClick={() => handleDisconnectAccount(account.school, account.username)}
                            className="action-button disconnect"
                          >
                            🔌 Disconnect
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Help Section */}
          <div className="instagram-help-section">
            <h3>ℹ️ How Instagram Connection Works</h3>
            <ul>
              <li><strong>No Test Users Needed:</strong> Since your app is in Live mode, any Instagram user can connect directly through OAuth.</li>
              <li><strong>Token Expiration:</strong> Long-lived tokens expire after 60 days. Refresh them before they expire to maintain access.</li>
              <li><strong>One Account Per School:</strong> Each school code can only have one active Instagram account at a time.</li>
              <li><strong>Business/Creator Accounts:</strong> Only Instagram Business or Creator accounts can be connected for posting.</li>
            </ul>
          </div>
        </div>
      )}

      {activeTab === 'abandoned-leads' && (
        <div className="tab-content">
          <div className="abandoned-leads-header">
            <h2>👤 Abandoned Leads</h2>
            <p className="abandoned-leads-description">
              Users who viewed tier prices but didn't complete their purchase. Check off users you've reached out to.
            </p>
            <button 
              onClick={fetchAbandonedLeads} 
              className="refresh-button"
              disabled={abandonedLeadsLoading}
            >
              🔄 Refresh
            </button>
          </div>

          {/* Stats */}
          <div className="abandoned-leads-stats">
            <div className="stat-pill total">
              <span className="stat-label">Total</span>
              <span className="stat-value">{abandonedLeadsCounts.total}</span>
            </div>
            <div className="stat-pill not-contacted">
              <span className="stat-label">Not Contacted</span>
              <span className="stat-value">{abandonedLeadsCounts.notContacted}</span>
            </div>
            <div className="stat-pill contacted">
              <span className="stat-label">Contacted</span>
              <span className="stat-value">{abandonedLeadsCounts.contacted}</span>
            </div>
          </div>

          {/* Sub-tabs */}
          <div className="abandoned-leads-subtabs">
            <button 
              className={`subtab ${abandonedLeadsTab === 'not-contacted' ? 'active' : ''}`}
              onClick={() => setAbandonedLeadsTab('not-contacted')}
            >
              📋 Not Contacted ({abandonedLeadsCounts.notContacted})
            </button>
            <button 
              className={`subtab ${abandonedLeadsTab === 'contacted' ? 'active' : ''}`}
              onClick={() => setAbandonedLeadsTab('contacted')}
            >
              ✅ Contacted ({abandonedLeadsCounts.contacted})
            </button>
          </div>

          {/* Loading State */}
          {abandonedLeadsLoading && (
            <div className="loading-container" style={{ minHeight: '200px' }}>
              <div className="loading-spinner"></div>
              <p>Loading abandoned leads...</p>
            </div>
          )}

          {/* Error State */}
          {abandonedLeadsError && (
            <div className="retry-message error">
              ❌ {abandonedLeadsError}
            </div>
          )}

          {/* Leads List */}
          {!abandonedLeadsLoading && !abandonedLeadsError && (
            <>
              {abandonedLeads.length === 0 ? (
                <div className="empty-state">
                  <p>
                    {abandonedLeadsTab === 'not-contacted' 
                      ? '🎉 No leads waiting to be contacted!' 
                      : '📭 No contacted leads yet'}
                  </p>
                  <p style={{ fontSize: '0.9rem', color: '#999', marginTop: '0.5rem' }}>
                    {abandonedLeadsTab === 'not-contacted'
                      ? 'New abandoned leads will appear here after the 3-hour cleanup runs'
                      : 'Check off leads from the "Not Contacted" tab as you reach out to them'}
                  </p>
                </div>
              ) : (
                <div className="abandoned-leads-list">
                  {abandonedLeads.map((lead) => (
                    <div key={lead.id} className={`abandoned-lead-card ${lead.contacted ? 'contacted' : ''}`}>
                      <div className="lead-checkbox">
                        <input
                          type="checkbox"
                          checked={lead.contacted}
                          onChange={(e) => handleMarkContacted(lead.id, e.target.checked)}
                          className="lead-checkbox-input"
                          title={lead.contacted ? 'Mark as not contacted' : 'Mark as contacted'}
                        />
                      </div>
                      <div className="lead-info">
                        <div className="lead-header">
                          <h3 className="lead-username">@{lead.instagram_handle}</h3>
                          <span className="school-badge">{lead.school}</span>
                        </div>
                        <div className="lead-details">
                          {lead.name && <p><strong>Name:</strong> {lead.name}</p>}
                          {lead.email && <p><strong>Email:</strong> {lead.email}</p>}
                          <p className="lead-date">
                            <strong>Abandoned:</strong> {formatDateTime(lead.original_created_at || lead.created_at)}
                          </p>
                          {lead.contacted_at && (
                            <p className="lead-contacted-date">
                              <strong>Contacted:</strong> {formatDateTime(lead.contacted_at)}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="lead-actions">
                        {lead.instagram_handle && (
                          <a
                            href={`https://instagram.com/${lead.instagram_handle}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="action-button view-ig"
                            title="View Instagram Profile"
                          >
                            📷 IG
                          </a>
                        )}
                        <button
                          onClick={() => handleDeleteLead(lead.id)}
                          className="action-button delete"
                          title="Delete lead"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Help Section */}
          <div className="abandoned-leads-help-section">
            <h3>ℹ️ About Abandoned Leads</h3>
            <ul>
              <li><strong>What are these?</strong> Users who filled out the submission form and viewed tier prices but didn't complete payment.</li>
              <li><strong>When are they captured?</strong> Every 3 hours, the cleanup system saves these leads before removing incomplete submissions.</li>
              <li><strong>Check off leads:</strong> Once you've reached out to a user, check their box to move them to the "Contacted" tab.</li>
              <li><strong>Instagram link:</strong> Click the IG button to open their Instagram profile in a new tab.</li>
            </ul>
          </div>
        </div>
      )}

      {activeTab === 'streamer' && (
        <div className="tab-content">
          <h2>UniStreaming</h2>
          {streamerLoading && <p>Loading...</p>}
          {streamerError && <p className="error-message">{streamerError}</p>}

          <div className="stats-grid streamer-stats-grid">
            <div className="stat-card highlight">
              <h3>Posted Successfully</h3>
              <div className="stat-number success">{streamerStats.posted}</div>
            </div>
            <div className="stat-card highlight">
              <h3>Skip the Line</h3>
              <div className="stat-number">{streamerStats.skip_line}</div>
            </div>
            <div className="stat-card highlight">
              <h3>Re-queued (Waiting)</h3>
              <div className="stat-number">{streamerStats.requeued}</div>
            </div>
            <div className="stat-card">
              <h3>Currently Posting</h3>
              <div className="stat-number">{streamerStats.posting}</div>
            </div>
            <div className="stat-card">
              <h3>Failed</h3>
              <div className="stat-number error">{streamerStats.failed}</div>
            </div>
          </div>

          <section className="streamer-admin-section">
            <h3>Pricing Tiers</h3>
            <p className="form-note">Streamer flow uses separate pricing from standard school submissions.</p>
            <div className="streamer-tiers-table">
              {streamerTiers.map((tier) => {
                const isEditing = editingStreamerTier?.id === tier.id;
                const editTier = isEditing ? editingStreamerTier : {
                  ...tier,
                  base_price_dollars: (tier.base_price / 100).toFixed(2),
                  min_price_dollars: (tier.min_price / 100).toFixed(2),
                  max_price_dollars: (tier.max_price / 100).toFixed(2)
                };

                return (
                  <div key={tier.id} className="streamer-tier-row">
                    {isEditing ? (
                      <>
                        <input
                          value={editTier.name}
                          onChange={(e) => setEditingStreamerTier({ ...editTier, name: e.target.value })}
                        />
                        <input
                          type="number"
                          step="0.01"
                          value={editTier.base_price_dollars}
                          onChange={(e) => setEditingStreamerTier({ ...editTier, base_price_dollars: e.target.value })}
                          placeholder="Price ($)"
                        />
                        <button onClick={() => handleSaveStreamerTier(editTier)}>Save</button>
                        <button onClick={() => setEditingStreamerTier(null)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <span><strong>{tier.name}</strong> — ${(tier.base_price / 100).toFixed(2)}</span>
                        <span className="tier-meta">{tier.description}</span>
                        <button onClick={() => setEditingStreamerTier({
                          ...tier,
                          base_price_dollars: (tier.base_price / 100).toFixed(2),
                          min_price_dollars: (tier.min_price / 100).toFixed(2),
                          max_price_dollars: (tier.max_price / 100).toFixed(2)
                        })}>Edit</button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          <section className="streamer-admin-section">
            <div className="streamer-submissions-header">
              <h3>Submissions ({streamerStats.visible_total})</h3>
              <p className="form-note">Incomplete not_queued drafts are hidden. Skip the Line listed first, then re-queued.</p>
            </div>
            <div className="streamer-submissions-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Platforms</th>
                    <th>Payment</th>
                    <th>Posting</th>
                    <th>Created</th>
                    <th>Posted</th>
                  </tr>
                </thead>
                <tbody>
                  {streamerSubmissions.length === 0 && !streamerLoading ? (
                    <tr>
                      <td colSpan={6}>No active streamer submissions</td>
                    </tr>
                  ) : (
                    streamerSubmissions.slice(0, 100).map((sub) => {
                      let platforms = sub.platforms;
                      if (typeof platforms === 'string') {
                        try { platforms = JSON.parse(platforms); } catch { platforms = []; }
                      }
                      const platformStr = Array.isArray(platforms)
                        ? platforms.map((p) => `${p.platform}:@${p.handle}`).join(', ')
                        : '—';
                      return (
                        <tr key={sub.id} className={`streamer-row-${sub.posting_status || 'unknown'}`}>
                          <td>{sub.name}</td>
                          <td>{platformStr}</td>
                          <td>{sub.payment_status || 'pending'}</td>
                          <td>
                            <span className={`streamer-status-badge status-${sub.posting_status || 'unknown'}`}>
                              {sub.posting_status || '—'}
                            </span>
                          </td>
                          <td>{formatDateTime(sub.created_at)}</td>
                          <td>{sub.posted_at ? formatDateTime(sub.posted_at) : '—'}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {activeTab === 'coming-soon' && (
        <div className="tab-content">
          <div className="manage-schools-header">
            <h2>⬛ Class of 2031 coming soon</h2>
            <button
              onClick={fetchComingSoonCampaign}
              className="refresh-button"
              disabled={comingSoonLoading}
            >
              🔄 Refresh
            </button>
          </div>

          <p className="coming-soon-intro">
            Enrolled accounts post a blank black image with the caption
            {' '}<strong>“Class of 2031 coming soon...”</strong> {comingSoon?.postsPerDay || 35} times
            per day, spread between {comingSoon?.window || '9:00am–11:00pm ET'}
            {comingSoon?.intervalMinutes ? ` (about every ${comingSoon.intervalMinutes} minutes)` : ''}.
            Only turn this on for accounts that should run this teaser — Instagram allows 50 posts per day.
          </p>

          {comingSoon && (
            <div className="coming-soon-status-row">
              <span className={`status-badge ${comingSoon.inWindow ? 'active' : 'inactive'}`}>
                {comingSoon.inWindow ? 'In posting window' : 'Outside 9am–11pm ET'}
              </span>
              <span className="coming-soon-status-meta">
                {comingSoon.accounts.filter((a) => a.enabled).length} enrolled
                {' · '}
                Expected {comingSoon.expectedToday}/{comingSoon.postsPerDay} posts by now today
              </span>
            </div>
          )}

          {comingSoonLoading && (
            <div className="loading-container" style={{ minHeight: '200px' }}>
              <div className="loading-spinner"></div>
              <p>Loading campaign…</p>
            </div>
          )}

          {comingSoonError && (
            <div className="retry-message error">
              ❌ {comingSoonError}
            </div>
          )}

          {!comingSoonLoading && !comingSoonError && comingSoon && (
            <div className="coming-soon-account-list">
              {comingSoon.accounts.length === 0 ? (
                <div className="empty-state">
                  <p>📭 No campus accounts found</p>
                </div>
              ) : (
                comingSoon.accounts.map((account) => {
                  const progress = Math.min(
                    100,
                    Math.round(((account.posts_today || 0) / (comingSoon.postsPerDay || 35)) * 100)
                  );
                  const toggling = Boolean(togglingComingSoon[account.school_code]);
                  return (
                    <div
                      key={account.school_code}
                      className={`coming-soon-account-row ${account.enabled ? 'enabled' : ''}`}
                    >
                      <div className="coming-soon-account-info">
                        <div className="coming-soon-account-title">
                          <strong>{account.school_code}</strong>
                          <span>{account.school_name}</span>
                        </div>
                        <div className="coming-soon-account-meta">
                          {account.instagram_connected
                            ? `Connected @${account.instagram_username}`
                            : 'Instagram not connected'}
                          {account.enabled && (
                            <>
                              {' · '}
                              {account.posts_today || 0}/{comingSoon.postsPerDay} today
                              {account.last_posted_at
                                ? ` · last ${formatDateTime(account.last_posted_at)}`
                                : ''}
                            </>
                          )}
                          {account.last_status === 'failed' && account.last_error && (
                            <span className="coming-soon-error"> · {account.last_error}</span>
                          )}
                        </div>
                        {account.enabled && (
                          <div className="coming-soon-progress-track">
                            <div
                              className="coming-soon-progress-fill"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        )}
                      </div>
                      <label className="coming-soon-switch">
                        <input
                          type="checkbox"
                          checked={Boolean(account.enabled)}
                          disabled={toggling || !account.is_active}
                          onChange={(e) => handleToggleComingSoon(account.school_code, e.target.checked)}
                        />
                        <span>{account.enabled ? 'On' : 'Off'}</span>
                      </label>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;

